import { datadogRum } from '@datadog/browser-rum';

import getConfigValue from '../config';

import { shouldDiscardRumEvent } from './rumEventFilter';

export const setDataDogUser = ({ id, username, email, organization }: Record<string, any>) => {
  datadogRum.setUser({
    id,
    username,
    email,
    organization,
  });
};

export const setDataDogUserProperty = (key: string, value: string) => {
  datadogRum.setUserProperty(key, value);
};

// Called at app bootstrap rather than from an authenticated wrapper: customer-facing routes
// (direct payment, change-order offers, invite-token flows) never mount one, and were invisible
// in RUM as a result.
export const initDataDog = () => {
  if (getConfigValue('NODE_ENV') === 'development') {
    console.log('DD RUM is disabled in local development mode');
    return;
  }

  if ((window as any)?.DD_RUM?.getInternalContext()) {
    return;
  }

  const datadogConfig = {
    appId: getConfigValue('VITE_APP_DD_APP_ID'),
    clientToken: getConfigValue('VITE_APP_DD_CLIENT_TOKEN'),
    envName: getConfigValue('VITE_APP_DD_ENV_NAME'),
    appVersion: getConfigValue('VITE_APP_VERSION'),
  };
  datadogRum.init({
    applicationId: datadogConfig.appId,
    clientToken: datadogConfig.clientToken,
    site: 'datadoghq.com',
    service: 'sabal-app',
    env: datadogConfig.envName,
    version: datadogConfig.appVersion,
    sessionSampleRate: 100,
    // Replay starts automatically during init: with a non-zero sample rate the SDK
    // defaults startSessionReplayRecordingManually to false. No explicit
    // startSessionReplayRecording() call is needed (or effective).
    sessionReplaySampleRate: 100,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [/https:\/\/(?!auth).+\.palmetto\.(com|finance)/],
    beforeSend: (event) => !shouldDiscardRumEvent(event),
  });
};

// Invite-token links land customers in the app unauthenticated, so their sessions carry no
// @usr.* attributes and are invisible to user-scoped RUM queries. Tag them with a global
// context so they're findable (@context.flow) and joinable to an account (@context.account_id).
export const tagInviteTokenSession = () => {
  // Guard here rather than relying on initDataDog running first: RUM is off in local dev.
  if (getConfigValue('NODE_ENV') === 'development') {
    return;
  }

  const inviteToken = new URLSearchParams(window.location.search).get('inviteToken');
  if (!inviteToken) {
    return;
  }

  datadogRum.setGlobalContextProperty('flow', 'invite-token');

  const accountId = /^\/accounts\/([^/]+)/.exec(window.location.pathname)?.[1];
  if (accountId) {
    datadogRum.setGlobalContextProperty('account_id', accountId);
  }
};
